import { ReactComponent as PipeFighterLogo } from '@bechtel/client-assets/dist/images/pf-logo-symbol.svg';
import { ReactComponent as PipeFighterLogoWithText } from '@bechtel/client-assets/dist/images/pipefighter-logo.svg';
import {
  AIIcon,
  EllipsisPlacement,
  EmulateAction,
  StatsSpinner,
  StatsSpinnerSize,
  TextWithEllipsis,
  useAppUser,
  useBlockPermission,
  useEnvProvider,
  useModulePermission,
} from '@bechtel/client-common';
import { useCrewChangePermissions, useCrewChangeStatsQuery } from '@bechtel/client-crew-change';
import { usePlanningPermissions } from '@bechtel/client-planning';
import { useProjectAdminPermissions } from '@bechtel/client-project-admin';
import { useReportPermissions } from '@bechtel/client-reports';
import { useTimesheetPermissions } from '@bechtel/client-timesheet';
import {
  Avatar,
  AvatarSize,
  Box,
  Icon,
  IconSize,
  Popover,
  Text,
  Theme,
  aboveBreakpoint,
  useMediaQuery,
} from '@bechtel/gds';
import { AuthPermission, AuthScope, CrewChangeRequestFilterByEnum, CrewChangeStat } from '@bechtel/types';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { addDays, formatISO } from 'date-fns';
import { get } from 'lodash';
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import {
  AvatarRegularStyled,
  ButtonNoTextStyled,
  ButtonStyled,
  CollapseIconStyled,
  EditProjectStyled,
  IconStyled,
  LinkNoTextStyled,
  LinkStyled,
  LogoStyled,
  NavBackdropStyled,
  NavBottomPlacementStyled,
  NavContainerStyled,
  NavStyled,
  NotificationStyled,
  UserSettingsIconStyled,
} from './styled';
import { NavButtonProps, NavComponentProps, NavListProps } from './types';

/* component that render each row of Nav  */
const NavComponent: FC<NavComponentProps> = ({
  navLink,
  navText,
  isExact,
  navIcon,
  isCollapsed,
  notificationValue = 0,
}) => {
  const [show, setShow] = useState<boolean>(false);
  const linkRef = useRef<HTMLDivElement>(null);
  const { colors, spacings } = useContext<Theme>(ThemeContext);
  const popoverBgColor = String(colors.blue[800]);

  if (!isCollapsed) {
    return (
      <LinkStyled to={navLink} end={isExact}>
        <Icon icon={navIcon} size={IconSize.LARGE} />
        <Box ml={{ sm: '600' }}>
          <Text weight="600" size="400" color="white" family="graphik">
            {navText}
          </Text>
        </Box>
        {notificationValue > 0 && <NotificationStyled>{notificationValue}</NotificationStyled>}
      </LinkStyled>
    );
  }
  return (
    <>
      <LinkNoTextStyled
        to={navLink}
        end={isExact}
        ref={linkRef}
        onMouseOver={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(false)}
      >
        {/* Seperate component as Navlink passes all the props to a tag */}
        <Icon icon={navIcon} size={IconSize.LARGE} />
        {notificationValue > 0 && (
          <NotificationStyled
            css={`
              margin-top: -${spacings[550]};
            `}
          >
            {notificationValue}
          </NotificationStyled>
        )}
      </LinkNoTextStyled>
      {/* zIndex set to 2 as progress bar has 1 */}
      <Popover
        triggerComponentRef={linkRef}
        placement="right"
        showPopper={show}
        customStyle={{ backgroundColor: popoverBgColor, zIndex: 2 }}
      >
        <Box px={{ sm: '500' }} py={{ sm: '450' }}>
          <Text style={{ whiteSpace: 'nowrap' }} family="graphik" size="400" color="white" weight="600">
            {navText}
          </Text>
        </Box>
      </Popover>
    </>
  );
};

/* Button-based nav component for non-route actions like AI Assistant */
const NavButtonComponent: FC<NavButtonProps> = ({ navText, navIcon, customIcon, isCollapsed, onClick }) => {
  const [show, setShow] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { colors } = useContext<Theme>(ThemeContext);
  const popoverBgColor = String(colors.blue[800]);

  const iconElement = customIcon || (navIcon && <Icon icon={navIcon} size={IconSize.LARGE} />);

  if (!isCollapsed) {
    return (
      <ButtonStyled onClick={onClick}>
        {iconElement}
        <Box ml={{ sm: '600' }}>
          <Text weight="600" size="400" color="white" family="graphik">
            {navText}
          </Text>
        </Box>
      </ButtonStyled>
    );
  }
  return (
    <>
      <ButtonNoTextStyled
        ref={buttonRef}
        onMouseOver={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => {
          setShow(false);
          onClick();
        }}
      >
        {iconElement}
      </ButtonNoTextStyled>
      <Popover
        triggerComponentRef={buttonRef}
        placement="right"
        showPopper={show}
        customStyle={{ backgroundColor: popoverBgColor, zIndex: 2 }}
      >
        <Box px={{ sm: '500' }} py={{ sm: '450' }}>
          <Text style={{ whiteSpace: 'nowrap' }} family="graphik" size="400" color="white" weight="600">
            {navText}
          </Text>
        </Box>
      </Popover>
    </>
  );
};

const NavList: FC<NavListProps> = ({ isCollapsed, toggleCollapse, toggle, onViewProfile, onOpenAIAssistant }) => {
  const { envConfig } = useEnvProvider();
  const { user, hasPermissionAndScope } = useAppUser();
  const { canViewBlocks } = useBlockPermission();
  const { i18n } = useLingui();
  const {
    moduleLoading,
    canViewPlanningModule,
    canViewCrewChangeModule,
    canViewTimeSheetModule,
    canViewBlockModule,
    canViewWorkPackageModule,
    canViewReportsModule,
  } = useModulePermission();
  const { canViewPlan } = usePlanningPermissions();
  const { canViewCrewChange } = useCrewChangePermissions();
  const { canViewProjectAdmin } = useProjectAdminPermissions();
  const { canViewTimesheet } = useTimesheetPermissions();
  const { canViewReports } = useReportPermissions();
  const canSwitchProject = hasPermissionAndScope(AuthPermission.SWITCH_PROJECT, AuthScope.APPLICATION);
  const canViewAIAssistant = hasPermissionAndScope(AuthPermission.VIEW_AI_ASSISTANT, AuthScope.APPLICATION);
  const enableEmulate = envConfig.enableEmulation.toLowerCase() === 'true' || user?.canEmulate;
  const noop = () => undefined;
  let crewChangePendingRequestCount = 0;

  const [getCrewChangeStats, { data: statData }] = useCrewChangeStatsQuery(true);

  useEffect(() => {
    if (!canViewCrewChange) return;
    setTimeout(() => {
      getCrewChangeStats({
        variables: {
          input: {
            startDate: formatISO(addDays(new Date(), -6), { representation: 'date' }),
            endDate: formatISO(new Date(), { representation: 'date' }),
          },
        },
      });
    }, 0);
  }, [canViewCrewChange, getCrewChangeStats]);

  if (canViewCrewChange) {
    const pendingCrewChangeStatsApprovals: CrewChangeStat[] = get(statData, 'crewChangeStats.approvals');
    if (pendingCrewChangeStatsApprovals && pendingCrewChangeStatsApprovals.length > 0) {
      const pendingCrewChangeStat = pendingCrewChangeStatsApprovals.find(
        (e) => e.statType == CrewChangeRequestFilterByEnum.PENDING_APPROVAL
      );
      crewChangePendingRequestCount =
        pendingCrewChangeStat && pendingCrewChangeStat.total ? pendingCrewChangeStat.total : 0;
    }
  }
  return (
    <>
      <LogoStyled isCollapsed={isCollapsed}>
        <Link to="/" data-testid="nav-test-id">
          {isCollapsed ? <PipeFighterLogo /> : <PipeFighterLogoWithText />}
        </Link>
        <EditProjectStyled
          onClick={canSwitchProject ? toggle : noop}
          isCollapsed={isCollapsed}
          canSwitchProject={canSwitchProject}
        >
          {!isCollapsed && (
            <Text size="200" weight="300" color="white" family="graphik">
              {user && user.project && user.project.name}
            </Text>
          )}
          {canSwitchProject && <Icon fillColor="white" icon="edit" size={IconSize.MEDIUM}></Icon>}
        </EditProjectStyled>
      </LogoStyled>
      <NavContainerStyled>
        <NavComponent
          isCollapsed={isCollapsed}
          navLink="/"
          navText={t`Dashboard`}
          isExact={true}
          navIcon="tachometer-fast"
        />
        {canViewWorkPackageModule && (
          <NavComponent
            isCollapsed={isCollapsed}
            navLink="/work-packages"
            navText={t`Work Packages`}
            isExact={false}
            navIcon="briefcase"
          />
        )}
        {canViewBlocks && canViewBlockModule && (
          <NavComponent
            isCollapsed={isCollapsed}
            navLink="/blocks"
            navText={t`Blocks`}
            isExact={false}
            navIcon="exclamation-triangle"
          />
        )}
        {canViewPlanningModule && canViewPlan && (
          <NavComponent
            isCollapsed={isCollapsed}
            navLink="/planning"
            navText={t`Planning`}
            isExact={false}
            navIcon="calendar-day"
          />
        )}
        {canViewCrewChangeModule && canViewCrewChange && (
          <NavComponent
            isCollapsed={isCollapsed}
            navLink="/crew-change"
            navText={t`Crew Change`}
            isExact={false}
            navIcon="users-class"
            notificationValue={crewChangePendingRequestCount}
          />
        )}
        {canViewTimeSheetModule && canViewTimesheet && (
          <NavComponent
            isCollapsed={isCollapsed}
            navLink="/timesheet"
            navText={t`Timesheet`}
            isExact={false}
            navIcon="user-clock"
          />
        )}
        {canViewProjectAdmin && (
          <NavComponent
            isCollapsed={isCollapsed}
            navLink="/project-admin"
            navText={t`Project Admin`}
            isExact={false}
            navIcon="cogs"
          />
        )}
        {canViewReportsModule && canViewReports && (
          <NavComponent
            isCollapsed={isCollapsed}
            navLink="/reports"
            navText={t`Reports`}
            isExact={false}
            navIcon="file-chart-column"
          />
        )}
        {moduleLoading && <StatsSpinner color="white" size={StatsSpinnerSize.MEDIUM} />}

        {/* AI Assistant */}
        {canViewAIAssistant && onOpenAIAssistant && (
          <NavButtonComponent
            isCollapsed={isCollapsed}
            navText={t`AI Assistant`}
            customIcon={<AIIcon size={24} color="white" />}
            onClick={onOpenAIAssistant}
          />
        )}
      </NavContainerStyled>
      <CollapseIconStyled isCollapsed={isCollapsed} onClick={toggleCollapse}>
        <Icon
          fillColor="white"
          icon={isCollapsed ? 'chevron-circle-right' : 'chevron-circle-left'}
          size={IconSize.LARGE}
        />
      </CollapseIconStyled>
      <NavBottomPlacementStyled>
        {!isCollapsed && (
          <>
            <Box justifyContent={{ sm: 'space-evenly' }} alignItems={{ sm: 'center' }} ml={{ sm: '500' }}>
              <AvatarRegularStyled src={user?.crewMember?.imageUrl} size={AvatarSize.MEDIUM} />
              {user && (
                <Box css={{ minWidth: '0', lineHeight: 'normal' }} flexDirection={{ sm: 'column' }}>
                  <TextWithEllipsis color="white" size="400" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
                    {user.firstName} {user.lastName}
                  </TextWithEllipsis>
                </Box>
              )}
              <UserSettingsIconStyled onClick={onViewProfile}>
                <IconStyled />
              </UserSettingsIconStyled>
              {!user && t`Profile`}
            </Box>

            {enableEmulate && <EmulateAction />}
          </>
        )}
        {isCollapsed && (
          <Box
            onClick={onViewProfile}
            justifyContent={{ sm: 'space-evenly' }}
            alignItems={{ sm: 'center' }}
            style={{ cursor: 'pointer' }}
          >
            <Avatar src={user?.crewMember?.imageUrl} size={AvatarSize.MEDIUM} />
          </Box>
        )}
      </NavBottomPlacementStyled>
    </>
  );
};

export const Nav = ({ toggle, onViewProfile, onOpenAIAssistant }) => {
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isAboveFullHD = useMediaQuery(aboveBreakpoint(breakpoints.fullHD));

  const [isCollapsed, setIsCollapse] = useState(!isAboveFullHD);

  useEffect(() => {
    setIsCollapse(!isAboveFullHD);
  }, [isAboveFullHD]);

  const toggleCollapse = useCallback((e) => {
    e.stopPropagation();
    return setIsCollapse((prev) => !prev);
  }, []);

  if (!isAboveFullHD && !isCollapsed) {
    return (
      <>
        <NavBackdropStyled onClick={toggleCollapse}>
          <NavStyled isCollapsed={isCollapsed}>
            <NavList
              toggleCollapse={toggleCollapse}
              isCollapsed={isCollapsed}
              toggle={toggle}
              onViewProfile={onViewProfile}
              onOpenAIAssistant={onOpenAIAssistant}
            />
          </NavStyled>
        </NavBackdropStyled>
        {/* Keep a collapsed placeholder in expanded mode so content panel doesn't move */}
        <NavStyled isCollapsed={true}></NavStyled>
      </>
    );
  } else {
    return (
      <NavStyled isCollapsed={isCollapsed}>
        <NavList
          toggleCollapse={toggleCollapse}
          isCollapsed={isCollapsed}
          toggle={toggle}
          onViewProfile={onViewProfile}
          onOpenAIAssistant={onOpenAIAssistant}
        />
      </NavStyled>
    );
  }
};
